import React, { Suspense, useCallback, useState } from 'react';
import { css } from '@emotion/css';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Loader } from '@lyearn/ui';
import { Dialog } from '@/components/Modals';
import useNavigation from '@/hooks/useNavigation';
import routes from '@/pages/corporate/routes';

import useFile from '../../../hooks/useFile';
import useFileActions from '../../../hooks/useFileActions';
import { DialogProps } from '../types';
import { ListEditorFormValues } from './components/Editor/config';
import ListEditorContainer from './ListEditorContainer';

export const dialogSize = css`
  max-width: 52rem;
  height: 62rem;
`;

function ListEditor({ onClose, fileId, folderId }: DialogProps) {
  const { t } = useTranslation('VisibilitySetting');
  const navigation = useNavigation();

  const [formikSubmit, setFormikSubmit] = useState<any>();
  const { state, methods } = useFileActions({ onSuccess: onClose });
  const { isFetching } = state;
  const { createList, updateList } = methods;

  const { fetching: fetchingFile, file, error } = useFile({ fileId });
  const onSubmit = useCallback(
    async (formValues: ListEditorFormValues) => {
      if (fileId) {
        updateList(fileId, formValues);
        return;
      }

      const response = await createList(formValues, folderId);

      if (response?.listId) {
        navigation.push(routes.TasksList(response.listId));
        return;
      }
    },
    [fileId, createList, folderId, updateList, navigation],
  );

  return (
    <Dialog
      open
      action={{
        disabled: isFetching || fetchingFile,
        isSubmitting: isFetching,
        text: file ? t('Common:Actions.Save') : t('Common:Actions.Create'),
        onSubmit: formikSubmit,
        buttonSize: 'large',
      }}
      className="h-full"
      classes={{ paper: cn('px-0 w-full rounded-16', dialogSize) }}
      closeButton={{
        size: 'large',
        className: 'end-12 top-20',
      }}
      header={{
        title: file ? t('Task:List.EditList') : t('Task:List.NewList'),
        description: file ? t('Task:List.EditListDescription') : t('Task:List.NewListDescription'),
        variants: {
          description: 'body-short-01',
        },
        className: 'pt-28 pb-24',
      }}
      onClose={onClose}>
      <Box className="px-24 pt-8 pb-24">
        <Suspense fallback={<Loader className="mx-auto my-auto" />}>
          <ListEditorContainer
            error={error}
            fetching={fetchingFile}
            file={file}
            setFormikSubmit={setFormikSubmit}
            onSubmit={onSubmit}
          />
        </Suspense>
      </Box>
    </Dialog>
  );
}

export default ListEditor;
